import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { WidgetService } from 'src/core/services/widget.service';
import { DeviceService } from 'src/core/services/device.service';
import { LoadingService } from 'src/core/services/loading.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { ChatService } from 'src/core/services/chat.service';
import { Table, TableModule } from 'primeng/table';
import { DaterangeService } from 'src/core/services/daterange.service';
import { defer, iif, map, merge, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { DataService } from 'src/core/services/data.service';
import { NgShuiModule } from '@shui/angular';
import { TranslocoModule } from '@jsverse/transloco';
import { selectedWidget, workareaSize } from 'src/core/types/workarea.types';
import { FormsModule } from '@angular/forms';
import { EscalationsData } from 'src/core/types/servicehistory.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from 'src/core/types/general.types';
import { SortEvent } from 'primeng/api';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { shouldMarkPromptAsOptionAnswer } from 'src/core/utils/chat-option-answer.util';

@Component({
  selector: 'app-escalations',
  imports: [CommonModule, TableModule, NgShuiModule, FormsModule, TranslocoModule],
  templateUrl: './escalations.component.html',
  styleUrl: './escalations.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EscalationsComponent {

  //Table Variables
  @ViewChild('table') table: Table;

  @Input() tabs: any[] = [];

  constructor(
    private widgetService: WidgetService,
    private deviceService: DeviceService,
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    private chatService: ChatService,
    private daterangeService: DaterangeService,
    private dataService: DataService,
    private selectedContentService: SelectedContentService
  ) { }

  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
  switchTab = this.widgetService.switchTab.bind(this.widgetService);
  changeRange = (range: number, type: 'systemHealth' | 'serviceHistory') => {
    this.daterangeService.changeRange(range, type);
  };

  screenInfo$ = this.deviceService.screenInfo$;
  isLoading: boolean;
  isError: boolean;
  deviceInformation: any;
  searchTerm: string = '';
  escalations: any[] = [];
  selectedRange: number;
  selectedWidget: string = "None";
  displayedTabs: any[] = [];
  activeTab: string = "Activities";
  workareaSize: workareaSize = "small";

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.loadingService.$isEscalationsLoading.subscribe((loading) => {
      this.isLoading = loading;
    });
    this.loadingService.$isEscalationsError.subscribe((error) => {
      this.isError = error;
    });

    //Check which tabs to display
    this.widgetService.$selectedWidget.subscribe({
      next: (widget: selectedWidget) => {
        this.selectedWidget = widget;
        this.displayedTabs = this.tabs.filter(tab => tab.selectedWidget === widget);
      }
    });

    //Check which tab is currently active
    this.widgetService.$activeTab.subscribe((tab) => {
      if (tab) {
        this.activeTab = tab;
      }
      else {
        this.activeTab = this.displayedTabs[0]?.name;
      }
    });

    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    //Listening to changed Daterange
    const range$ = this.daterangeService.$selectedRangeServiceHistory;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshEscalations.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );

    triggers$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isEscalationsLoading.next(true);
        this.loadingService.$isEscalationsError.next(false);
        this.escalations = null;
      }),
      switchMap(({ rangeValue, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.EscalationsPullDate;
        const pulledFor = this.selectedSystemService.escalationsPulledFor;

        const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';

        const shouldFetch =
          force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== (serial + material);

        return iif(
          () => shouldFetch,
          this.dataService.getEscalations(material, serial).pipe(
            tap(({ response, status }: ApiResponse<EscalationsData>) => {
              console.log(force ? 'Forced refresh of escalations data' : 'Fetching new escalations data');
              this.selectedSystemService.EscalationsPullDate = now;
              this.selectedSystemService.escalationsPulledFor = serial + material;
              if (status === 200) {
                this.selectedSystemService.$escalations.next(response);
              } else if (status === 204) {
                this.selectedSystemService.$escalations.next(null);
                this.loadingService.$isEscalationsEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$escalations.pipe(
            take(1),
            tap(() => console.log('Using cached escalation data')),
            map((cached): ApiResponse<EscalationsData> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response, status }: ApiResponse<EscalationsData>) => ({ response, rangeValue }))
        );
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      {
        next: ({ response, rangeValue }) => {

          this.selectedRange = rangeValue;

          const temp = response?.escalations || [];

          this.escalations = temp.filter(f => f.time_window.some(tw => tw === rangeValue));

          this.loadingService.$isEscalationsLoading.next(false);

        },
        error: (error) => {
          console.error('Error fetching spare parts data:', error);
          this.escalations = [];
          this.loadingService.$isEscalationsError.next(true);
          this.loadingService.$isEscalationsLoading.next(false);
        }
      }
    );
  }




  askChat(id: string) {

    let prompt = `What can you tell me about the Service Ticket with ID ${id}?`;

    if (shouldMarkPromptAsOptionAnswer(this.chatService.messages?.value)) {
      this.chatService.addMessage(prompt, true, undefined, true);
      return;
    }

    this.chatService.addMessage(prompt, true);

  }

  onSearchApply(event: any) {
    this.searchTerm = event.value?.toLowerCase() || '';
  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  refresh() {
    this.selectedSystemService.$refreshEscalations.next();
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'opened': // assuming this is a date field
          val1 = new Date(a.opened);
          val2 = new Date(b.opened);
          break;
        case 'closed': // assuming this is a date field
          val1 = new Date(a.closed);
          val2 = new Date(b.closed);
          break;

        case 'peak': // string field
          val1 = a.peak?.toLowerCase() ?? '';
          val2 = b.peak?.toLowerCase() ?? '';
          break;
        case 'description': // string field
          val1 = a.short_text?.toLowerCase() ?? '';
          val2 = b.short_text?.toLowerCase() ?? '';
          break;
        case 'lvl': // string field
          val1 = a.level?.toLowerCase() ?? '';
          val2 = b.level?.toLowerCase() ?? '';
          break;
        case 'noti': // string field
          val1 = a.notification?.toLowerCase() ?? '';
          val2 = b.notification?.toLowerCase() ?? '';
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

}
